/**
 * Kalender — Top-Level-Page (Welle-3-Modul, 2026-06-30, Cluster 17.2).
 *
 * Aktueller Stand (2026-07-03):
 * - Month + Week + Day-View (View-Switcher), Range-Load je nach View
 * - Aggregat-Events aus 5 Quellen: personal, tasks, absences, todos, fuhrpark
 * - Personal-Event-Create per „+"-Knopf
 * - Klick auf Event → Read-Only-Quelle navigiert zu Quell-Modul,
 *   Personal-Event oeffnet Edit-Modal
 * - Scopes-Filter (5 Toggle-Chips) — welche Quellen anzeigen. Zustand in
 *   localStorage persistiert, wird als `?scopes=...` an /aggregate gehaengt.
 * - iCal-Subscribe (⚙-Button in der Header-Leiste) — Modal mit
 *   KalenderSubscribeCard fuer .ics-Token-Verwaltung
 *
 * Out-of-Scope (Folge-Wellen):
 * - Recurrence-Rules (wiederkehrende Termine mit RRULE)
 * - Weitere Cross-Modul-Quellen bei Bedarf (Bibliothek-Ablaeufe,
 *   Service-Desk-Termine, Chat-erwaehnte Termine)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Plus, ChevronLeft, ChevronRight, X, Loader2, Calendar as CalIcon, Settings2, Play, Square } from 'lucide-react';
import { api, apiFetch } from '@onvice/portal-shell-api';
import KalenderSubscribeCard from '../components/KalenderSubscribeCard';
import { RecurrenceEditor } from '@onvice/portal-ui';
import { useFederatedModule } from '../lib/useFederatedModule';

// switchTimer dynamisch aus bauberichteV2 — statischer Import wuerde den
// Federation-Bootstrap-Preload zwingen und einen bauberichteV2-Ausfall zum
// globalen Portal-Boot-Kill machen. Regel:
// [[feedback_federation_no_static_imports]].
type BauberichteV2Public = {
  switchTimer: (opts: { taskId: string | null }) => Promise<unknown>;
};

/** Aktiver Timer-Snapshot vom BV2-Backend — leichter Ausschnitt. */
interface ActiveTimerLite { taskId: string | null }

type ViewMode = 'month' | 'week' | 'day' | 'list';
type ScopeKey = 'personal' | 'tasks' | 'absences' | 'todos' | 'fuhrpark' | 'ressourcen' | 'holidays';

/**
 * Scopes mit Anzeige-Metadaten. `key` matched das Backend
 * (`kalender/routes.ts` `SCOPE_DEFAULTS`). `color` MUSS mit der Event-Farbe
 * im Backend uebereinstimmen (`kalender/routes.ts` loadPersonal/loadTasks/...) —
 * sonst zeigen Chip und Event-Bullets unterschiedliche Farben und der User
 * kann Filter+Grid nicht mehr optisch verknuepfen.
 */
const ALL_SCOPES: Array<{ key: ScopeKey; label: string; color: string }> = [
  { key: 'personal', label: 'Eigene Termine', color: '#3b82f6' },  // blue-500
  { key: 'tasks',    label: 'Aufgaben',        color: '#f59e0b' },  // amber-500
  { key: 'absences', label: 'Abwesenheiten',   color: '#ef4444' },  // red-500
  { key: 'todos',    label: 'Todos',           color: '#10b981' },  // emerald-500
  { key: 'fuhrpark', label: 'Fuhrpark',        color: '#a855f7' },  // purple-500
  { key: 'ressourcen', label: 'Ressourcen',    color: '#0891b2' },  // cyan-600 (Wartungen)
  { key: 'holidays', label: 'Feiertage',       color: '#eab308' },  // amber-500 (Highlight)
];
const SCOPES_STORAGE_KEY = 'kalender.visibleScopes';
const DEFAULT_SCOPES: ScopeKey[] = ALL_SCOPES.map((s) => s.key);

function loadVisibleScopes(): ScopeKey[] {
  if (typeof window === 'undefined') return DEFAULT_SCOPES;
  try {
    const raw = localStorage.getItem(SCOPES_STORAGE_KEY);
    if (!raw) return DEFAULT_SCOPES;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return DEFAULT_SCOPES;
    const valid = parsed.filter((v): v is ScopeKey =>
      typeof v === 'string' && DEFAULT_SCOPES.includes(v as ScopeKey),
    );
    // Wenn alle Scopes weg-geschaltet waeren → als Default alle anzeigen
    // (User verliert sonst versehentlich den Kalender komplett).
    return valid.length > 0 ? valid : DEFAULT_SCOPES;
  } catch {
    return DEFAULT_SCOPES;
  }
}
function persistVisibleScopes(scopes: ScopeKey[]): void {
  try { localStorage.setItem(SCOPES_STORAGE_KEY, JSON.stringify(scopes)); } catch { /* ignore */ }
}

interface AggregatedEvent {
  id: string;
  source: 'personal' | 'task' | 'absence' | 'todo' | 'fuhrpark' | 'ressourcen' | 'holiday';
  source_module: string;
  source_id: string;
  is_recurring_instance?: boolean;
  title: string;
  start_at: string;
  end_at: string | null;
  all_day: boolean;
  location: string | null;
  link_url: string;
  color: string;
  read_only: boolean;
}

export default function KalenderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const bv2 = useFederatedModule<BauberichteV2Public>('bauberichteV2/public');
  const switchTimer = bv2?.switchTimer ?? null;
  // 2026-07-13 (Joerg): Zurueck-Nav aus Detail-Seite muss zur GENAUEN
  // Kalenderansicht zurueck (view=list vs day). setView macht replace:true, damit
  // History nicht mit View-Wechseln zumuellt — Nebeneffekt: Browser-Back nach
  // Detail-Klick landet auf /kalender ohne view-Param und wird durch den
  // Mobile-Default zu Tagesansicht umgelenkt. Fix: beim Klick auf einen
  // Read-Only-Termin den vollen Pfad+Search als location.state.from
  // mitgeben. useBack() in Detail-Seiten bevorzugt diesen from-Wert vor
  // navigate(-1).
  const goToEvent = useCallback((url: string) => {
    navigate(url, { state: { from: location.pathname + location.search } });
  }, [navigate, location.pathname, location.search]);
  // SD-2026-00034 (Onvice, 2026-07-31): Zentraler Klick-Handler statt inline
  // `if (e.read_only) goToEvent(...)`. Regeln:
  //  - Feiertag: nichts tun (link_url ist nur `/kalender` ohne Tag — Sprung
  //    wuerde Kalender auf Heute zuruecksetzen, User verliert Kontext).
  //  - Personal-Termin: Edit-Modal oeffnen (bearbeitbar + loeschbar).
  //  - Alle anderen read_only-Quellen (Task/Absence/Todo/Fuhrpark/Ressourcen):
  //    zu Quell-Modul navigieren wie bisher.
  const handleEventClick = useCallback((e: AggregatedEvent) => {
    if (e.source === 'holiday') return;
    if (e.source === 'personal') { setEditingEventId(e.source_id); return; }
    if (e.read_only) goToEvent(e.link_url);
  }, [goToEvent]);
  // Phase 6 (2026-06-30): Mobile-Default ist Day-View — Month-Grid ist auf
  // kleinen Screens unbedienbar. URL-Param hat Vorrang, damit der User das
  // explizit ueberschreiben kann.
  useEffect(() => {
    if (searchParams.get('view')) return;
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(max-width: 768px)').matches) {
      const next = new URLSearchParams(searchParams);
      next.set('view', 'day');
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const view: ViewMode = ((): ViewMode => {
    const v = searchParams.get('view');
    // 2026-07-13 (Joerg/Alex+Kiara v7): 'list' war im Type erlaubt, aber der
    // Parser hat es nicht erkannt und still auf 'month' geworfen — dadurch
    // landete der User nach Zurueck-Klick aus Aufgabe nicht in der Liste,
    // sondern (nach Mount-Effekt-Umlenkung auf Mobile) in Tagesansicht.
    if (v === 'week' || v === 'day' || v === 'list') return v;
    return 'month';
  })();
  const setView = (v: ViewMode): void => {
    const next = new URLSearchParams(searchParams);
    if (v === 'month') next.delete('view');
    else next.set('view', v);
    // 2026-07-13 (Joerg/Alex/Kiara v6): push statt replace — Browser-Back
    // aus einer Detail-Seite muss die zuletzt sichtbare Kalender-View
    // reproduzieren. Bisher wurde jeder View-Wechsel per replace direkt in
    // die aktuelle History-Position geschrieben; nach navigate(-1) landete
    // man auf der Mount-Auto-Umstellung (view=day auf Mobile) — nicht auf
    // der Liste, aus der der User zur Aufgabe gesprungen war.
    setSearchParams(next);
  };
  // Portalweite Suche (PS-4, 2026-09-09): /kalender?date=YYYY-MM-DD springt
  // direkt zum Monat/Tag des Treffers statt auf Heute.
  const dateParam = ((): Date | null => {
    const raw = searchParams.get('date');
    if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const d = new Date(`${raw}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  })();
  const [cursor, setCursor] = useState<Date>(() => firstOfMonth(dateParam ?? new Date()));
  const [events, setEvents] = useState<AggregatedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(() => stripTime(dateParam ?? new Date()));
  // SD-2026-00034 (Onvice, 2026-07-31): Klick auf Personal-Termin oeffnet
  // Edit-Modal (vorher: Klick auf non-read_only-Event tat nichts).
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  // Scopes-Filter (2026-07-03): welche der 5 Quellen (personal/tasks/absences/
  // todos/fuhrpark) im Grid gezeigt werden. Zustand in localStorage. Wird als
  // ?scopes=X,Y an /aggregate gehaengt — Backend filtert on-the-fly.
  const [visibleScopes, setVisibleScopes] = useState<ScopeKey[]>(() => loadVisibleScopes());
  const setScopesAndPersist = useCallback((next: ScopeKey[]): void => {
    setVisibleScopes(next);
    persistVisibleScopes(next);
  }, []);
  // Aktiver BV2-Timer — fuer Inline-Play/Stop in der Liste. null = kein Timer
  // laeuft. Bei taskId-Match rendern wir Stop, sonst Play. Refresh nach jeder
  // switchTimer-Aktion.
  const [activeTimer, setActiveTimer] = useState<ActiveTimerLite | null>(null);
  const loadActiveTimer = useCallback(async (): Promise<void> => {
    try {
      const r = await apiFetch<{ success: boolean; data: { taskId: string | null } | null }>('/modules/bauberichteV2/timer/active');
      setActiveTimer(r?.data ? { taskId: r.data.taskId } : null);
    } catch { /* fail-soft: Timer-Feature deaktiviert wenn BV2 nicht da */ }
  }, []);
  useEffect(() => { void loadActiveTimer(); }, [loadActiveTimer]);
  const toggleTaskTimer = useCallback(async (taskId: string): Promise<void> => {
    if (!switchTimer) {
      console.warn('[Kalender] bauberichteV2 nicht geladen — Timer-Toggle nicht verfuegbar');
      return;
    }
    const isActive = activeTimer?.taskId === taskId;
    try {
      // switchTimer feuert bei suggestGpsUpdate ein Portal-Event — hier ohne
      // GPS-Capture (Kalender ist nicht das Terrain fuer Standort-Erfassung).
      await switchTimer({ taskId: isActive ? null : taskId });
      await loadActiveTimer();
      // Aggregate braucht nicht neu geladen zu werden — Task-Zeilen aendern
      // sich durch switchTimer nicht (nur time_entries wachsen). Der Marker
      // im Grid haengt an activeTimer, wird durch loadActiveTimer aktualisiert.
    } catch (err) {
      console.warn('[Kalender] Timer-Switch fehlgeschlagen:', (err as Error).message);
    }
  }, [activeTimer?.taskId, loadActiveTimer, switchTimer]);

  const toggleScope = useCallback((key: ScopeKey): void => {
    const next = visibleScopes.includes(key)
      ? visibleScopes.filter((s) => s !== key)
      : [...visibleScopes, key];
    // Nie alle deaktivieren — sonst leerer Kalender ohne Recovery
    if (next.length === 0) return;
    setScopesAndPersist(next);
  }, [visibleScopes, setScopesAndPersist]);

  const monthRange = useMemo(() => {
    // Lade je nach View — fuer Month grosse Range, fuer Week +-1 Woche, fuer Day +-1 Tag.
    const anchor = view === 'month' ? firstOfMonth(cursor) : (selectedDay ?? cursor);
    const from = new Date(anchor);
    const to = new Date(anchor);
    if (view === 'month') {
      from.setDate(from.getDate() - 7);
      to.setMonth(to.getMonth() + 1);
      to.setDate(to.getDate() + 7);
    } else if (view === 'week') {
      from.setDate(from.getDate() - 7);
      to.setDate(to.getDate() + 14);
    } else if (view === 'list') {
      // Liste: 7 Tage zurueck, 90 Tage vorwaerts — analog BV2-Kalender.
      from.setDate(from.getDate() - 7);
      to.setDate(to.getDate() + 90);
    } else {
      from.setDate(from.getDate() - 1);
      to.setDate(to.getDate() + 1);
    }
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [cursor, view, selectedDay]);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const scopesParam = visibleScopes.join(',');
      const r = await api.get<{ success: boolean; events: AggregatedEvent[] }>(
        `/kalender/aggregate?from=${encodeURIComponent(monthRange.from)}&to=${encodeURIComponent(monthRange.to)}&scopes=${encodeURIComponent(scopesParam)}`,
      );
      setEvents(r.events ?? []);
    } catch (err) {
      // PWA-Offline (Jörg-Fund 2026-09-09): Netzfehler nicht als rohes
      // „Failed to fetch" zeigen — der Read-Cache liefert bereits den letzten
      // Stand des Zeitraums; ohne Cache-Treffer gibt es einen deutschen
      // Hinweis statt Entwickler-Jargon.
      const isNetErr = err instanceof TypeError
        || (err instanceof Error && !('status' in err) && /fetch|network|load failed/i.test(err.message));
      setError(isNetErr
        ? 'Offline — dieser Zeitraum wurde noch nicht geladen. Angezeigt wird der zuletzt bekannte Stand.'
        : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [monthRange, visibleScopes]);

  useEffect(() => { void load(); }, [load]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, AggregatedEvent[]>();
    for (const e of events) {
      const start = stripTime(new Date(e.start_at));
      const end = e.end_at ? stripTime(new Date(e.end_at)) : start;
      // Multi-Day-Event: pro Tag im Bereich eintragen.
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = isoDay(d);
        const list = map.get(key) ?? [];
        list.push(e);
        map.set(key, list);
      }
    }
    return map;
  }, [events]);

  const selectedDayEvents = selectedDay ? (eventsByDay.get(isoDay(selectedDay)) ?? []) : [];
  const weekDays = useMemo(() => weekFromAnchor(selectedDay ?? cursor), [selectedDay, cursor]);

  const goPrev = (): void => {
    if (view === 'month') setCursor(addMonths(cursor, -1));
    else if (view === 'week') {
      const next = new Date(selectedDay ?? cursor);
      next.setDate(next.getDate() - 7);
      setSelectedDay(stripTime(next));
      setCursor(firstOfMonth(next));
    } else {
      const next = new Date(selectedDay ?? cursor);
      next.setDate(next.getDate() - 1);
      setSelectedDay(stripTime(next));
      setCursor(firstOfMonth(next));
    }
  };
  const goNext = (): void => {
    if (view === 'month') setCursor(addMonths(cursor, 1));
    else if (view === 'week') {
      const next = new Date(selectedDay ?? cursor);
      next.setDate(next.getDate() + 7);
      setSelectedDay(stripTime(next));
      setCursor(firstOfMonth(next));
    } else {
      const next = new Date(selectedDay ?? cursor);
      next.setDate(next.getDate() + 1);
      setSelectedDay(stripTime(next));
      setCursor(firstOfMonth(next));
    }
  };
  const goToday = (): void => {
    const t = new Date();
    setSelectedDay(stripTime(t));
    setCursor(firstOfMonth(t));
  };

  const periodLabel = view === 'month' ? formatMonth(cursor)
    : view === 'week'  ? formatWeekLabel(weekDays)
    : view === 'list'  ? 'Liste (–7 / +90 Tage)'
    : formatDayHeader(selectedDay ?? cursor);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <CalIcon size={22} /> Kalender
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          {/* View-Switcher */}
          <div className="flex items-center gap-0.5 p-0.5 rounded-md bg-muted">
            {(['month', 'week', 'day', 'list'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  view === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {v === 'month' ? 'Monat' : v === 'week' ? 'Woche' : v === 'day' ? 'Tag' : 'Liste'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={goPrev}
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
            title="Zurueck"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="font-medium min-w-[12rem] text-center text-sm">{periodLabel}</span>
          <button
            type="button"
            onClick={goNext}
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
            title="Vorwaerts"
          >
            <ChevronRight size={18} />
          </button>
          <button
            type="button"
            onClick={goToday}
            className="px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground"
          >
            Heute
          </button>
          <button
            type="button"
            onClick={() => setSubscribeOpen(true)}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
            title="Kalender-Abos verwalten (iCal-Feeds)"
          >
            <Settings2 size={16} />
          </button>
          <button
            type="button"
            onClick={() => setNewOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={16} /> Termin
          </button>
        </div>
      </div>

      {/* Scopes-Filter — als Chip-Row unter der Header-Leiste. Zeigt/versteckt
          welche der 5 Aggregat-Quellen im Grid erscheinen. Farbpunkt matcht
          die Farbrichtung im Grid (Event-Farbe kommt weiter vom Backend). */}
      <div className="mb-3 flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-muted-foreground mr-1">Anzeigen:</span>
        {ALL_SCOPES.map((s) => {
          const active = visibleScopes.includes(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggleScope(s.key)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs transition-colors ${
                active
                  ? 'bg-primary/10 text-foreground border border-primary/40'
                  : 'bg-muted text-muted-foreground border border-transparent hover:text-foreground'
              }`}
              title={active ? `„${s.label}" ausblenden` : `„${s.label}" einblenden`}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: active ? s.color : 'transparent', border: active ? 'none' : `1px solid ${s.color}` }}
              />
              {s.label}
            </button>
          );
        })}
      </div>

      {error && <div className="mb-3 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{error}</div>}

      {view === 'month' && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          <div className="rounded-lg border border-default bg-card overflow-hidden">
            <div className="grid grid-cols-7 bg-muted text-xs font-semibold text-muted-foreground">
              {['Mo','Di','Mi','Do','Fr','Sa','So'].map((d) => (
                <div key={d} className="px-2 py-1.5 text-center">{d}</div>
              ))}
            </div>
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
                <Loader2 size={16} className="animate-spin" /> Laedt...
              </div>
            ) : (
              <MonthGrid
                cursor={cursor}
                eventsByDay={eventsByDay}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
              />
            )}
          </div>

          <aside className="rounded-lg border border-default bg-card p-3">
            <div className="text-sm font-semibold mb-2">
              {selectedDay ? formatDayHeader(selectedDay) : 'Tag waehlen'}
            </div>
            <DayEventList events={selectedDayEvents} onEventClick={handleEventClick} />
          </aside>
        </div>
      )}

      {view === 'week' && (
        <div className="rounded-lg border border-default bg-card overflow-hidden">
          <div className="grid grid-cols-7 bg-muted text-xs font-semibold text-muted-foreground">
            {weekDays.map((d) => {
              const isToday = isoDay(d) === isoDay(new Date());
              return (
                <button
                  key={isoDay(d)}
                  type="button"
                  onClick={() => setSelectedDay(d)}
                  className={`px-2 py-1.5 text-center hover:bg-card transition-colors ${isToday ? 'text-primary font-bold' : ''}`}
                >
                  {d.toLocaleDateString('de-DE', { weekday: 'short' })} {d.getDate()}.
                </button>
              );
            })}
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
              <Loader2 size={16} className="animate-spin" /> Laedt...
            </div>
          ) : (
            <div className="grid grid-cols-7 divide-x divide-default min-h-[400px]">
              {weekDays.map((d) => {
                const key = isoDay(d);
                const list = eventsByDay.get(key) ?? [];
                const isToday = key === isoDay(new Date());
                return (
                  <div key={key} className={`p-1.5 space-y-1 ${isToday ? 'bg-primary/5' : ''}`}>
                    {list.length === 0 && <div className="text-xs text-muted-foreground italic">—</div>}
                    {list.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => handleEventClick(e)}
                        className="w-full text-left px-1.5 py-1 rounded text-xs hover:opacity-80"
                        style={{ backgroundColor: `${e.color}25`, color: e.color, borderLeft: `3px solid ${e.color}` }}
                        title={e.is_recurring_instance ? `${e.title} (Serie)` : e.title}
                      >
                        <div className="font-medium truncate">{e.is_recurring_instance ? '🔁 ' : ''}{e.title}</div>
                        {!e.all_day && <div className="opacity-70">{formatTime(e.start_at)}</div>}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === 'day' && (
        <div
          className="rounded-lg border border-default bg-card p-4 min-h-[400px] touch-pan-y"
          onTouchStart={(e) => {
            const t = e.touches[0];
            if (t) (e.currentTarget as HTMLElement & { _swipeX?: number })._swipeX = t.clientX;
          }}
          onTouchEnd={(e) => {
            const t = e.changedTouches[0];
            const startX = (e.currentTarget as HTMLElement & { _swipeX?: number })._swipeX;
            if (!t || startX == null) return;
            const dx = t.clientX - startX;
            if (Math.abs(dx) < 60) return;
            if (dx < 0) goNext(); else goPrev();
          }}
        >
          <div className="text-sm font-semibold mb-3 text-muted-foreground">
            {selectedDay ? formatDayHeader(selectedDay) : 'Heute'}
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
              <Loader2 size={16} className="animate-spin" /> Laedt...
            </div>
          ) : (
            <DayEventList events={selectedDayEvents} onEventClick={handleEventClick} large />
          )}
        </div>
      )}

      {view === 'list' && (
        <div className="rounded-lg border border-default bg-card overflow-hidden">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
              <Loader2 size={16} className="animate-spin" /> Laedt...
            </div>
          ) : events.length === 0 ? (
            <div className="text-sm text-muted-foreground italic py-12 text-center">Keine Ereignisse im Zeitraum.</div>
          ) : (
            <ListView
              events={events}
              activeTaskId={activeTimer?.taskId ?? null}
              onToggleTimer={(taskId) => void toggleTaskTimer(taskId)}
              onEventClick={handleEventClick}
            />
          )}
        </div>
      )}

      {newOpen && (
        <NewEventModal
          defaultDay={selectedDay ?? new Date()}
          onClose={() => setNewOpen(false)}
          onCreated={() => { setNewOpen(false); void load(); }}
        />
      )}

      {subscribeOpen && (
        <SubscribeModal onClose={() => setSubscribeOpen(false)} />
      )}

      {editingEventId && (
        <EditEventModal
          eventId={editingEventId}
          onClose={() => setEditingEventId(null)}
          onSaved={() => { setEditingEventId(null); void load(); }}
          onDeleted={() => { setEditingEventId(null); void load(); }}
        />
      )}
    </div>
  );
}

/**
 * Modal-Wrapper um die vorhandene <KalenderSubscribeCard/>. Card wird
 * unveraendert eingebettet — Rendering, Fetch, Create, Delete kapselt sie
 * selbst. Hier nur Overlay + Schliess-Kreuz + Klick-outside-to-close.
 */
function SubscribeModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 p-4 pt-16 sm:pt-24 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg bg-card border border-default shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-default">
          <h2 className="text-base font-semibold">Kalender-Abos verwalten</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schliessen">
            <X size={18} />
          </button>
        </div>
        <div className="p-4">
          <KalenderSubscribeCard />
          <div className="mt-3 text-xs text-muted-foreground">
            Ein Abo liefert alle Ereignisse aus den gewaehlten Scopes als .ics-Feed.
            Kopiere die URL in deinen Kalender (Outlook, iCal, Google Kalender)
            als „Kalender abonnieren".
          </div>
        </div>
      </div>
    </div>
  );
}

interface MonthGridProps {
  cursor: Date;
  eventsByDay: Map<string, AggregatedEvent[]>;
  selectedDay: Date | null;
  onSelectDay: (d: Date) => void;
}

function MonthGrid({ cursor, eventsByDay, selectedDay, onSelectDay }: MonthGridProps) {
  const first = firstOfMonth(cursor);
  const startWeekday = (first.getDay() + 6) % 7;  // Mo=0 ... So=6
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;
  const today = isoDay(new Date());
  const selectedKey = selectedDay ? isoDay(selectedDay) : null;

  const cells: { day: Date; inMonth: boolean }[] = [];
  for (let i = 0; i < totalCells; i += 1) {
    const d = new Date(first);
    d.setDate(d.getDate() + (i - startWeekday));
    cells.push({ day: d, inMonth: d.getMonth() === cursor.getMonth() });
  }

  return (
    <div className="grid grid-cols-7">
      {cells.map(({ day, inMonth }, i) => {
        const key = isoDay(day);
        const list = eventsByDay.get(key) ?? [];
        const isToday = key === today;
        const isSelected = selectedKey === key;
        return (
          <button
            type="button"
            key={i}
            onClick={() => onSelectDay(day)}
            className={`min-h-[80px] border-t border-l border-default p-1.5 text-left flex flex-col gap-0.5 transition-colors ${
              isSelected ? 'bg-primary/10 ring-1 ring-primary/40 ring-inset' :
              isToday    ? 'bg-primary/5' :
              inMonth    ? 'hover:bg-muted/50' :
                           'bg-muted/30 text-muted-foreground'
            }`}
          >
            <div className={`text-xs font-medium ${isToday ? 'text-primary' : ''}`}>{day.getDate()}</div>
            <div className="flex flex-col gap-0.5 overflow-hidden">
              {list.slice(0, 3).map((e) => (
                <div
                  key={e.id}
                  className="text-[10px] leading-tight truncate flex items-center gap-1"
                  title={e.is_recurring_instance ? `${e.title} (Serie)` : e.title}
                >
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: e.color }} />
                  <span className="truncate">{e.is_recurring_instance ? '🔁 ' : ''}{e.title}</span>
                </div>
              ))}
              {list.length > 3 && (
                <div className="text-[10px] text-muted-foreground">+{list.length - 3} mehr</div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

interface NewEventProps {
  defaultDay: Date;
  onClose: () => void;
  onCreated: () => void;
}

function NewEventModal({ defaultDay, onClose, onCreated }: NewEventProps) {
  const [title, setTitle] = useState('');
  const [startAt, setStartAt] = useState(() => toLocalInput(combineDateTime(defaultDay, 9, 0)));
  const [endAt, setEndAt] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [location, setLocation] = useState('');
  // Recurrence: Toggle + RRULE-String. Der Editor liefert den Rule-String,
  // wir schicken ihn als recurrence_rule ans Backend. Bei rrule=null bleibt
  // der Termin einmalig.
  const [recurring, setRecurring] = useState(false);
  const [rrule, setRrule] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (): Promise<void> => {
    const t = title.trim();
    if (!t) { setError('Titel fehlt'); return; }
    if (!startAt) { setError('Start-Zeit fehlt'); return; }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/kalender/events', {
        title: t,
        start_at: new Date(startAt).toISOString(),
        end_at: endAt ? new Date(endAt).toISOString() : null,
        all_day: allDay,
        location: location.trim() || null,
        recurrence_rule: recurring ? rrule : null,
      });
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 p-4 pt-24" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-card border border-default shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-default">
          <h2 className="text-base font-semibold">Neuer Termin</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="px-4 py-4 space-y-3">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Titel"
            autoFocus
            className="w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Start</label>
            <input
              type="datetime-local"
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
              className="w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Ende (optional)</label>
            <input
              type="datetime-local"
              value={endAt}
              onChange={(e) => setEndAt(e.target.value)}
              className="w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
            ganztaegig
          </label>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Ort (optional)"
            className="w-full h-10 px-3 rounded-md border border-default bg-background text-foreground"
          />
          <label className="flex items-center gap-2 text-sm pt-1 border-t border-default">
            <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
            <span>🔁 Wiederholt sich</span>
          </label>
          {recurring && (
            <RecurrenceEditor
              startDate={startAt ? new Date(startAt) : defaultDay}
              value={rrule}
              onChange={setRrule}
            />
          )}
          {error && <div className="text-sm text-danger">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-default">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground">Abbrechen</button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />} Anlegen
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * SD-2026-00034 (Onvice, 2026-07-31): Bearbeiten + Loeschen selbst erstellter
 * Personal-Termine. Laedt via GET /kalender/events/:id (owner_user_id-gefiltert),
 * speichert via PATCH, loescht via DELETE mit confirm.
 */
function EditEventModal({ eventId, onClose, onSaved, onDeleted }: {
  eventId: string;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [location, setLocation] = useState('');
  const [rrule, setRrule] = useState<string | null>(null);
  const [recurring, setRecurring] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const r = await api.get<{ success: boolean; event: {
          title: string; start_at: string; end_at: string | null; all_day: boolean;
          location: string | null; recurrence_rule: string | null;
        } }>(`/kalender/events/${eventId}`);
        if (aborted) return;
        setTitle(r.event.title);
        setStartAt(toLocalInput(new Date(r.event.start_at)));
        setEndAt(r.event.end_at ? toLocalInput(new Date(r.event.end_at)) : '');
        setAllDay(!!r.event.all_day);
        setLocation(r.event.location ?? '');
        setRrule(r.event.recurrence_rule);
        setRecurring(!!r.event.recurrence_rule);
      } catch (err) {
        if (!aborted) setError((err as Error).message);
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [eventId]);

  const submit = async (): Promise<void> => {
    const t = title.trim();
    if (!t) { setError('Titel fehlt'); return; }
    if (!startAt) { setError('Start-Zeit fehlt'); return; }
    setSubmitting(true);
    setError('');
    try {
      await apiFetch(`/kalender/events/${eventId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: t,
          start_at: new Date(startAt).toISOString(),
          end_at: endAt ? new Date(endAt).toISOString() : null,
          all_day: allDay,
          location: location.trim() || null,
          recurrence_rule: recurring ? rrule : null,
        }),
      });
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async (): Promise<void> => {
    if (!window.confirm('Termin wirklich loeschen?')) return;
    setDeleting(true);
    setError('');
    try {
      await apiFetch(`/kalender/events/${eventId}`, { method: 'DELETE' });
      onDeleted();
    } catch (err) {
      setError((err as Error).message);
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 p-4 pt-24" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-card border border-default shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-default">
          <h2 className="text-base font-semibold">Termin bearbeiten</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
            <Loader2 size={16} className="animate-spin" /> Laedt...
          </div>
        ) : (
          <>
            <div className="px-4 py-4 space-y-3">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Titel"
                autoFocus
                className="w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Start</label>
                <input
                  type="datetime-local"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  className="w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Ende (optional)</label>
                <input
                  type="datetime-local"
                  value={endAt}
                  onChange={(e) => setEndAt(e.target.value)}
                  className="w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
                ganztaegig
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Ort (optional)"
                className="w-full h-10 px-3 rounded-md border border-default bg-background text-foreground"
              />
              <label className="flex items-center gap-2 text-sm pt-1 border-t border-default">
                <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
                <span>🔁 Wiederholt sich</span>
              </label>
              {recurring && (
                <RecurrenceEditor
                  startDate={startAt ? new Date(startAt) : new Date()}
                  value={rrule}
                  onChange={setRrule}
                />
              )}
              {error && <div className="text-sm text-danger">{error}</div>}
            </div>
            <div className="flex justify-between gap-2 px-4 py-3 border-t border-default">
              <button
                type="button"
                onClick={() => void doDelete()}
                disabled={deleting || submitting}
                className="px-3 py-1.5 text-sm text-danger hover:bg-danger/10 rounded-md disabled:opacity-50"
              >
                {deleting ? 'Loescht...' : 'Loeschen'}
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground">Abbrechen</button>
                <button
                  type="button"
                  onClick={() => void submit()}
                  disabled={submitting || deleting}
                  className="inline-flex items-center gap-2 px-4 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {submitting && <Loader2 size={14} className="animate-spin" />} Speichern
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DayEventList({ events, onEventClick, large }: { events: AggregatedEvent[]; onEventClick: (e: AggregatedEvent) => void; large?: boolean }) {
  if (events.length === 0) {
    return <div className="text-sm text-muted-foreground italic">Keine Termine.</div>;
  }
  return (
    <ul className={large ? 'space-y-2' : 'space-y-1.5'}>
      {events.map((e) => (
        <li key={e.id}>
          <button
            type="button"
            onClick={() => onEventClick(e)}
            className={`w-full text-left rounded-md flex items-center gap-3 ${
              large ? 'p-3 border border-default bg-background' : 'px-2 py-1.5'
            } hover:bg-muted ${e.source !== 'holiday' ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: e.color }} />
            <div className="flex-1 min-w-0">
              <div className={large ? 'font-medium' : 'font-medium truncate text-sm'}>
                {e.is_recurring_instance && <span title="Wiederkehrender Termin (Serie)" className="mr-1">🔁</span>}
                {e.title}
              </div>
              <div className="text-xs text-muted-foreground">
                {e.all_day ? 'ganztaegig' : formatTime(e.start_at)}
                {e.location ? ` · ${e.location}` : ''}
              </div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * List-View — chronologische Liste aller Events im Range, gruppiert nach Tag.
 * Bei source='task' zusaetzlich Inline-Timer-Button (Play/Stop), der ueber
 * switchTimer den BV2-Timer schaltet. Wenn der User schon einen Timer auf
 * genau dieser Task laufen hat: Stop-Button.
 */
function ListView({ events, activeTaskId, onToggleTimer, onEventClick }: {
  events: AggregatedEvent[];
  activeTaskId: string | null;
  onToggleTimer: (taskId: string) => void;
  onEventClick: (e: AggregatedEvent) => void;
}) {
  // Nach Tag gruppieren (isoDay des start_at). Aggregat kommt bereits sortiert.
  const groups = new Map<string, AggregatedEvent[]>();
  for (const e of events) {
    const key = isoDay(new Date(e.start_at));
    const list = groups.get(key) ?? [];
    list.push(e);
    groups.set(key, list);
  }
  const keys = Array.from(groups.keys()).sort();
  const todayIso = isoDay(new Date());
  return (
    <div className="divide-y divide-default">
      {keys.map((key) => {
        const list = groups.get(key)!;
        const dateObj = new Date(key + 'T00:00:00');
        const isToday = key === todayIso;
        return (
          <div key={key}>
            <div className={`px-4 py-2 text-xs font-medium ${isToday ? 'bg-primary/5 text-primary' : 'bg-muted/30 text-muted-foreground'}`}>
              {dateObj.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
              {isToday && <span className="ml-2 text-[10px] uppercase tracking-wide">heute</span>}
            </div>
            <ul>
              {list.map((e) => {
                const isTask = e.source === 'task';
                const isActive = isTask && activeTaskId === e.source_id;
                return (
                  <li key={e.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-muted/30 transition-colors">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: e.color }} />
                    <button
                      type="button"
                      onClick={() => onEventClick(e)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <div className="text-sm font-medium truncate">
                        {e.is_recurring_instance && <span title="Wiederkehrend" className="mr-1">🔁</span>}
                        {e.title}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {e.all_day ? 'ganztaegig' : formatTime(e.start_at)}
                        {e.location ? ` · ${e.location}` : ''}
                      </div>
                    </button>
                    {isTask && (
                      <button
                        type="button"
                        onClick={() => onToggleTimer(e.source_id)}
                        className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                          isActive
                            ? 'bg-danger/15 text-danger hover:bg-danger/25'
                            : 'bg-primary/15 text-primary hover:bg-primary/25'
                        }`}
                        title={isActive ? 'Timer stoppen' : 'Timer starten'}
                      >
                        {isActive ? <><Square size={12} /> Stop</> : <><Play size={12} /> Start</>}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function firstOfMonth(d: Date): Date {
  const r = new Date(d);
  r.setDate(1);
  r.setHours(0, 0, 0, 0);
  return r;
}
function addMonths(d: Date, n: number): Date {
  const r = new Date(d);
  r.setMonth(r.getMonth() + n);
  return r;
}
function stripTime(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}
function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function formatMonth(d: Date): string {
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}
function weekFromAnchor(anchor: Date): Date[] {
  // Liefert die 7 Tage der Woche (Mo–So) in der `anchor` liegt.
  const monday = new Date(anchor);
  const dow = (monday.getDay() + 6) % 7;  // Mo=0 ... So=6
  monday.setDate(monday.getDate() - dow);
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return d;
  });
}
function formatWeekLabel(week: Date[]): string {
  const a = week[0]!;
  const b = week[6]!;
  return `${a.getDate()}. – ${b.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}`;
}
function formatDayHeader(d: Date): string {
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}
function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}
function combineDateTime(d: Date, h: number, m: number): Date {
  const r = new Date(d);
  r.setHours(h, m, 0, 0);
  return r;
}
function toLocalInput(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
